import {
  TRAFFIC_CAPS,
  TRAFFIC_MINUTE_RETENTION_DAYS,
  type TrafficTopDimension,
  type TrafficTopRow,
} from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { lookupAddresses } from '../api/addresses.js';
import { getTrafficTop, type TrafficScope } from '../api/traffic.js';
import { blockRuleLink } from '../lib/block-page.js';
import {
  activeFilters,
  applyFilters,
  countriesIn,
  FILTERS_FOR,
  NO_FILTERS,
  type BotCheck,
  type TrafficFilters,
} from '../lib/traffic-filter.js';
import type { TrafficRisk } from '../lib/traffic-risk.js';
import { countryName, flagOf } from '../lib/country.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { SelectField, TextField } from '../ui/Field.js';
import { Tabs, TabPanel, type TabItem } from '../ui/Tabs.js';
import { useTab } from '../ui/use-tab.js';
import { EmptyState } from './EmptyState.js';
import { GeoAttribution } from './GeoAttribution.js';
import { BanAction, BotActions, KeyCell, type Actor } from './traffic-parts.js';
import {
  errorsColumn,
  hasRisk,
  refusedColumn,
  requestsColumn,
  riskColumn,
} from './traffic-columns.js';
import { SiteName, SitePanel, type SiteActions } from './TrafficSites.js';
import { TrafficRowDetail } from './TrafficRowDetail.js';

/**
 * The six lists as one table with a tab for each kind (P11.15): sites, pages,
 * addresses, bots, browsers and how requests were answered. One set of filters
 * serves them all — a filter that means nothing on a list is not offered there —
 * and every row opens into what is joined to it and what can be done about it.
 *
 * What is filtered is what the agents reported: the heaviest of each list, per
 * minute. A filter narrows that; it cannot reach requests the lists never held.
 */

type View = 'host' | 'path' | 'ip' | 'bot' | 'ua_family' | 'status';

const VIEWS: readonly TabItem<View>[] = [
  { id: 'host', label: 'Sites' },
  { id: 'path', label: 'Pages' },
  { id: 'ip', label: 'Addresses' },
  { id: 'bot', label: 'Bots' },
  { id: 'ua_family', label: 'Browsers' },
  { id: 'status', label: 'Answers' },
];

const NAME: Readonly<Record<View, string>> = {
  host: 'Site',
  path: 'Page',
  ip: 'Address',
  bot: 'Bot',
  ua_family: 'Browser or client',
  status: 'Answer',
};

const RISKS: readonly { value: TrafficRisk; label: string }[] = [
  { value: 'none', label: 'Any' },
  { value: 'low', label: 'Low or worse' },
  { value: 'medium', label: 'Medium or worse' },
  { value: 'high', label: 'High or worse' },
  { value: 'critical', label: 'Critical' },
];

const MIN_REQUESTS = [0, 10, 100, 1000] as const;

const BOT_CHECKS: readonly { value: BotCheck; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'verified', label: 'Verified' },
  { value: 'fake', label: 'Fake' },
  { value: 'unchecked', label: 'Not checked' },
];

export function TrafficExplorer({
  scope,
  actor,
  canChange,
  siteActions,
}: {
  scope: TrafficScope;
  actor: Actor | undefined;
  /** Whether this person may make rules. */
  canChange: boolean;
  siteActions: SiteActions;
}): ReactNode {
  const [view, setView] = useTab(VIEWS, 'view');
  const [filters, setFilters] = useState<TrafficFilters>(NO_FILTERS);
  const [site, setSite] = useState<TrafficTopRow | null>(null);
  const set = (patch: Partial<TrafficFilters>): void => {
    setFilters((current) => ({ ...current, ...patch }));
  };

  const dimension: TrafficTopDimension = view;
  const top = useQuery({
    queryKey: ['traffic-top', scope, dimension, 'explorer-tab'],
    queryFn: () => getTrafficTop(scope, dimension, TRAFFIC_CAPS[dimension]),
    placeholderData: keepPreviousData,
  });
  const all = top.data?.rows ?? [];

  const ips = view === 'ip' ? all.map((row) => row.key) : [];
  const statuses = useQuery({
    queryKey: ['address-lookup', ips],
    queryFn: () => lookupAddresses(ips),
    enabled: ips.length > 0,
  });
  const statusOf = new Map((statuses.data ?? []).map((entry) => [entry.ip, entry]));

  const rows = applyFilters(all, dimension, filters, statusOf);
  const total = all.reduce((sum, row) => sum + row.requests, 0);
  const offered = FILTERS_FOR[dimension];
  const active = activeFilters(filters);
  const countries = view === 'ip' ? countriesIn(all) : [];

  const columns: Column<TrafficTopRow>[] = [
    {
      key: 'name',
      header: NAME[view],
      rowHeader: true,
      sortValue: (row) => row.key,
      cell: (row) =>
        view === 'host' ? (
          <SiteName host={row.key} />
        ) : (
          <KeyCell dimension={dimension} row={row} status={statusOf.get(row.key)} />
        ),
    },
    requestsColumn(total),
    ...(view === 'status' ? [] : [refusedColumn(true), errorsColumn(true)]),
    ...(hasRisk(dimension) ? [riskColumn()] : []),
    ...(view === 'status'
      ? []
      : [
          {
            key: 'speed',
            header: 'Avg response',
            align: 'end' as const,
            wideOnly: true,
            sortValue: (row: TrafficTopRow) => row.avgResponseMs,
            cell: (row: TrafficTopRow) =>
              `${Math.round(row.avgResponseMs).toLocaleString('en-US')} ms`,
          },
        ]),
    ...(actor !== undefined && (view === 'ip' || view === 'bot')
      ? [
          {
            key: 'act',
            header: 'Next',
            cell: (row: TrafficTopRow) =>
              view === 'bot' ? (
                <BotActions botKey={row.key} actor={actor} />
              ) : (
                <BanAction ip={row.key} actor={actor} />
              ),
          },
        ]
      : []),
  ];

  return (
    <Card
      title="Explore the traffic"
      description="One list for each kind of thing that asked: filter it, open a row for who and what is joined to it, and act on it there."
      aside={
        <Link to={`/traffic/${dimension}${window.location.search}`} className="text-sm">
          Open the whole list →
        </Link>
      }
    >
      <Tabs
        label="Kinds of traffic"
        tabs={VIEWS}
        current={view}
        onSelect={setView}
        idPrefix="traffic"
      />
      <TabPanel idPrefix="traffic" current={view}>
        <div className="filters traffic-filters">
          <TextField
            label="Search"
            type="search"
            placeholder={view === 'ip' ? 'Address, network or country' : 'Name'}
            value={filters.q}
            onChange={(event) => {
              set({ q: event.target.value });
            }}
          />
          {offered.includes('risk') && (
            <SelectField
              label="Risk"
              value={filters.minRisk}
              onChange={(event) => {
                set({ minRisk: event.target.value as TrafficRisk });
              }}
            >
              {RISKS.map((risk) => (
                <option key={risk.value} value={risk.value}>
                  {risk.label}
                </option>
              ))}
            </SelectField>
          )}
          <SelectField
            label="Asked at least"
            value={String(filters.minRequests)}
            onChange={(event) => {
              set({ minRequests: Number(event.target.value) });
            }}
          >
            {MIN_REQUESTS.map((n) => (
              <option key={n} value={n}>
                {n === 0 ? 'Any number' : `${n.toLocaleString('en-US')} requests`}
              </option>
            ))}
          </SelectField>
          {offered.includes('country') && (
            <SelectField
              label="Country"
              value={filters.country}
              onChange={(event) => {
                set({ country: event.target.value });
              }}
            >
              <option value="">Any country</option>
              {countries.map((code) => (
                <option key={code} value={code}>
                  {flagOf(code)} {countryName(code)}
                </option>
              ))}
            </SelectField>
          )}
          {offered.includes('botCheck') && (
            <SelectField
              label="Claim"
              value={filters.botCheck}
              onChange={(event) => {
                set({ botCheck: event.target.value as BotCheck });
              }}
            >
              {BOT_CHECKS.map((check) => (
                <option key={check.value} value={check.value}>
                  {check.label}
                </option>
              ))}
            </SelectField>
          )}
          {(offered.includes('refused') || offered.includes('stopped')) && (
            <fieldset className="filter-checks">
              <legend className="visually-hidden">Only show</legend>
              {offered.includes('refused') && (
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={filters.onlyRefused}
                    onChange={(event) => {
                      set({ onlyRefused: event.target.checked });
                    }}
                  />
                  Mostly refused
                </label>
              )}
              {offered.includes('stopped') && (
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={filters.notStopped}
                    onChange={(event) => {
                      set({ notStopped: event.target.checked });
                    }}
                  />
                  Not stopped yet
                </label>
              )}
            </fieldset>
          )}
          <Button
            disabled={active === 0}
            onClick={() => {
              setFilters(NO_FILTERS);
            }}
          >
            {active === 0 ? 'Clear' : `Clear (${String(active)})`}
          </Button>
        </div>

        {top.isError && (
          <Alert tone="danger" title="Could not load this list">
            Try again in a moment.
          </Alert>
        )}
        {top.data !== undefined && (
          <>
            <p className="muted text-sm" aria-live="polite">
              {rows.length === all.length
                ? `${rows.length.toLocaleString('en-US')} ${view === 'status' ? 'answers' : 'shown'}`
                : `${rows.length.toLocaleString('en-US')} of ${all.length.toLocaleString('en-US')} shown`}
              {view === 'ip' &&
                ` · kept ${String(TRAFFIC_MINUTE_RETENTION_DAYS)} days, so a longer range shows the last ${String(TRAFFIC_MINUTE_RETENTION_DAYS)}`}
            </p>
            {all.some((row) => row.geo !== undefined) && <GeoAttribution />}
            <DataTable
              caption={`${NAME[view]} list, by requests`}
              columns={columns}
              rows={rows}
              rowKey={(row) => row.key}
              pagination={{}}
              initialSort={{ key: 'requests', direction: 'desc' }}
              {...(view === 'host'
                ? {
                    onSelect: setSite,
                    selectedKey: site?.key,
                  }
                : {})}
              renderDetail={(row) => (
                <TrafficRowDetail
                  dimension={dimension}
                  row={row}
                  scope={scope}
                  blockPage={
                    canChange
                      ? (path) =>
                          blockRuleLink(path, {
                            serverId: scope.serverId,
                            groupId: scope.groupId,
                          })
                      : undefined
                  }
                  addressAction={
                    actor === undefined ? undefined : (ip) => <BanAction ip={ip} actor={actor} />
                  }
                />
              )}
              empty={
                all.length === 0 ? (
                  <EmptyState title="No requests in this range">Pick a longer range.</EmptyState>
                ) : (
                  <EmptyState title="Nothing matches these filters">
                    Loosen a filter, or clear them.
                  </EmptyState>
                )
              }
            />
          </>
        )}
        {site !== null && (
          <SitePanel
            site={site}
            scope={scope}
            actions={siteActions}
            onClose={() => {
              setSite(null);
            }}
          />
        )}
      </TabPanel>
    </Card>
  );
}
